import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  CircularProgress,
  Snackbar,
  Alert,
  Tooltip,
} from '@mui/material';
import { Add, Edit, Delete, Map as MapIcon, AccountBalanceWallet, TrendingUp } from '@mui/icons-material';
import {
  PageHeader,
  StatCard,
  TableSurface,
  headerCellSx,
  bodyRowSx,
  StatusDot,
  StatusTone,
  ErrorState,
  TableEmptyState,
} from '../../components/common/pageKit';
import { useChartTokens } from '../../dashboard/chartTokens';
import { formatIndianNumber } from '../../utils/currency';
import crmPlusService, { Territory, CreateTerritoryInput } from '../../services/crmPlusService';

/**
 * Territories & Performance — CRUD over sales territories, each carrying a
 * live rollup (pipeline, revenue, attainment) computed server-side. This
 * page never fabricates the rollup numbers; it only renders what
 * getTerritories() returns.
 */

const isNum = (v: any): v is number => v !== null && v !== undefined && !isNaN(Number(v));
const fmtMoney = (v: any) => (isNum(v) ? formatIndianNumber(Number(v)) : '—');
const fmtPct = (v: any) => (isNum(v) ? `${Number(v).toFixed(1)}%` : '—');
const fmtInt = (v: any) => (isNum(v) ? String(Number(v)) : '—');

const attainmentTone = (pct: number | null | undefined): StatusTone => {
  if (pct === null || pct === undefined || isNaN(Number(pct))) return 'neutral';
  const p = Number(pct);
  if (p >= 100) return 'success';
  if (p >= 70) return 'warning';
  return 'error';
};

interface TerritoryFormData {
  name: string;
  region: string;
  states: string;
  ownerId: string;
  targetAmount: string;
}

const emptyForm: TerritoryFormData = { name: '', region: '', states: '', ownerId: '', targetAmount: '' };

const TerritoriesPage: React.FC = () => {
  const T = useChartTokens();

  const [territories, setTerritories] = useState<Territory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTerritory, setEditingTerritory] = useState<Territory | null>(null);
  const [form, setForm] = useState<TerritoryFormData>(emptyForm);
  const [saving, setSaving] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState<Territory | null>(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    crmPlusService
      .getTerritories()
      .then((res) => setTerritories(res.territories || []))
      .catch((e: any) => setError(e?.message || 'Failed to load territories.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const totals = useMemo(() => {
    if (!territories.length) return { count: 0, openPipeline: 0, revenueFY: 0 };
    return territories.reduce(
      (acc, t) => ({
        count: acc.count + 1,
        openPipeline: acc.openPipeline + (Number(t.openPipelineValue) || 0),
        revenueFY: acc.revenueFY + (Number(t.revenueThisFY) || 0),
      }),
      { count: 0, openPipeline: 0, revenueFY: 0 }
    );
  }, [territories]);

  const openCreateDialog = () => {
    setEditingTerritory(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEditDialog = (t: Territory) => {
    setEditingTerritory(t);
    setForm({
      name: t.name,
      region: t.region || '',
      states: t.states || '',
      ownerId: t.ownerId ? String(t.ownerId) : '',
      targetAmount: t.targetAmount !== null && t.targetAmount !== undefined ? String(t.targetAmount) : '',
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload: CreateTerritoryInput = {
        name: form.name.trim(),
        region: form.region.trim() || undefined,
        states: form.states.trim() || undefined,
        ownerId: form.ownerId.trim() ? parseInt(form.ownerId, 10) : null,
        targetAmount: form.targetAmount.trim() ? parseFloat(form.targetAmount) : null,
      };
      if (editingTerritory) {
        await crmPlusService.updateTerritory(editingTerritory.id, payload);
        setToast({ message: 'Territory updated', severity: 'success' });
      } else {
        await crmPlusService.createTerritory(payload);
        setToast({ message: 'Territory created', severity: 'success' });
      }
      setDialogOpen(false);
      load();
    } catch (err: any) {
      setToast({ message: err?.message || 'Failed to save territory', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await crmPlusService.deleteTerritory(deleteTarget.id);
      setToast({ message: 'Territory deleted', severity: 'success' });
      setDeleteTarget(null);
      load();
    } catch (err: any) {
      setToast({ message: err?.message || 'Failed to delete territory', severity: 'error' });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Box>
      <PageHeader
        title="Territories"
        subtitle="Sales territories with live pipeline, revenue and quota-attainment rollups"
        actions={
          <Button variant="contained" startIcon={<Add />} size="small" onClick={openCreateDialog}>
            New Territory
          </Button>
        }
      />

      {loading && territories.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={28} />
        </Box>
      ) : error && territories.length === 0 ? (
        <ErrorState message={error} onRetry={load} />
      ) : (
        <>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: 'repeat(1, minmax(0, 1fr))', sm: 'repeat(3, minmax(0, 1fr))' },
              gap: 2,
              mb: 2.5,
            }}
          >
            <StatCard
              title="Total Territories"
              value={totals.count}
              icon={<MapIcon />}
              color={T.series(0)}
            />
            <StatCard
              title="Total Open Pipeline"
              value={fmtMoney(totals.openPipeline)}
              icon={<AccountBalanceWallet />}
              color={T.series(1)}
            />
            <StatCard
              title="Total Revenue (FY)"
              value={fmtMoney(totals.revenueFY)}
              icon={<TrendingUp />}
              color={T.status.good}
            />
          </Box>

          <TableSurface>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    {[
                      'Territory',
                      'Region',
                      'Owner',
                      'Customers',
                      'Open Pipeline',
                      'Weighted',
                      'Revenue (FY)',
                      'Target',
                      'Attainment',
                      '',
                    ].map((h, i) => (
                      <TableCell key={h || `col-${i}`} align={i >= 3 && i <= 8 ? 'right' : 'left'} sx={headerCellSx}>
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {territories.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={10} sx={{ border: 0 }}>
                        <TableEmptyState
                          title="No territories yet"
                          hint="Create a territory to start tracking regional pipeline and quota attainment."
                        />
                      </TableCell>
                    </TableRow>
                  ) : (
                    territories.map((t) => (
                      <TableRow key={t.id} sx={bodyRowSx}>
                        <TableCell sx={{ py: 1.5 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {t.name}
                          </Typography>
                        </TableCell>
                        <TableCell>{t.region || '—'}</TableCell>
                        <TableCell>{t.ownerName || '—'}</TableCell>
                        <TableCell align="right">{fmtInt(t.customerCount)}</TableCell>
                        <TableCell align="right">{fmtMoney(t.openPipelineValue)}</TableCell>
                        <TableCell align="right">{fmtMoney(t.weightedPipelineValue)}</TableCell>
                        <TableCell align="right">{fmtMoney(t.revenueThisFY)}</TableCell>
                        <TableCell align="right">{fmtMoney(t.targetAmount)}</TableCell>
                        <TableCell align="right">
                          <StatusDot label={fmtPct(t.attainmentPct)} tone={attainmentTone(t.attainmentPct)} />
                        </TableCell>
                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => openEditDialog(t)}>
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton size="small" onClick={() => setDeleteTarget(t)}>
                              <Delete fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </TableSurface>
        </>
      )}

      {/* Create / edit dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingTerritory ? 'Edit Territory' : 'New Territory'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              fullWidth
              label="Territory Name"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              required
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                label="Region"
                value={form.region}
                onChange={(e) => setForm((f) => ({ ...f, region: e.target.value }))}
              />
              <TextField
                fullWidth
                label="States"
                value={form.states}
                onChange={(e) => setForm((f) => ({ ...f, states: e.target.value }))}
                helperText="Comma-separated"
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                label="Owner (User ID)"
                value={form.ownerId}
                onChange={(e) => setForm((f) => ({ ...f, ownerId: e.target.value.replace(/[^0-9]/g, '') }))}
                helperText="Optional — numeric user ID"
              />
              <TextField
                fullWidth
                label="Target Amount"
                value={form.targetAmount}
                onChange={(e) => setForm((f) => ({ ...f, targetAmount: e.target.value.replace(/[^0-9.]/g, '') }))}
                helperText="Optional — annual target"
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={saving || !form.name.trim()}>
            {saving ? 'Saving…' : editingTerritory ? 'Save Changes' : 'Create Territory'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Delete Territory</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete "{deleteTarget?.name}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>
            Cancel
          </Button>
          <Button onClick={handleDelete} color="error" variant="contained" disabled={deleting}>
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!toast} autoHideDuration={4000} onClose={() => setToast(null)}>
        {toast ? (
          <Alert severity={toast.severity} onClose={() => setToast(null)} variant="filled">
            {toast.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
};

export default TerritoriesPage;
